import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  csvDownloadURL,
  endpoints,
  type CampaignActionPayload,
  type DomainReputationItem,
  type ReviewSponsorshipRequest,
  type SponsorshipRow,
} from "@/lib/api";
import { decodeJSONB, fmtDate, fmtTimestamp, platformLabel } from "@/lib/format";
import { PlatformBadge } from "@/components/PlatformBadge";
import {
  Badge,
  decisionVariant,
  decisionLabel,
  confidenceVariant,
  confidenceLabel,
} from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ChannelPicker } from "@/components/ChannelPicker";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Sponsorship Review Queue — SPEC §7. Operator confirms / rejects each
// detected sponsorship; confirmations flow into competitor_overlap metric
// (90-day window in scoring engine).
//
// Layout: filters at top, list on left, detail (with detection_signals) +
// confirm/reject buttons on right.
// ---------------------------------------------------------------------------

const REVIEW_STATUSES = [
  { value: "pending", label: "待审" },
  { value: "", label: "全部" },
  { value: "confirmed", label: "已确认" },
  { value: "rejected", label: "已拒绝" },
];

// v2.7.4 — promotion-style filter. Default "not_link_only" hides the
// noisy affiliate-harvest pattern (e.g. @nicogrowthz eSIM tutorials
// with Binance link buried in description) from the pending queue so
// the operator's attention stays on actual content endorsements.
// Operator can switch to "" to see everything, or zoom into one class.
const PROMOTION_STYLES = [
  { value: "not_link_only", label: "隐藏挂链型" },
  { value: "", label: "全部类型" },
  { value: "content_focused", label: "仅内容型" },
  { value: "mixed", label: "仅混合型" },
  { value: "link_only", label: "仅挂链型" },
];

// Short Chinese labels shown as inline badges per row. Empty string
// (no rule classification yet) renders nothing — backfill or next
// detection run will fill it in.
function promotionStyleLabel(style: string | null): string {
  switch (style) {
    case "content_focused": return "内容型";
    case "link_only": return "挂链型";
    case "mixed": return "混合型";
    default: return "";
  }
}

function promotionStyleClass(style: string | null): string {
  switch (style) {
    case "content_focused":
      // Green-ish tone — high-signal, operator should look at these.
      return "text-emerald-700 dark:text-emerald-400";
    case "link_only":
      // Muted/orange — low-priority noise that's been demoted.
      return "text-amber-600 dark:text-amber-500";
    case "mixed":
      // Neutral — needs human read.
      return "text-muted-foreground";
    default:
      return "text-muted-foreground";
  }
}

const PAGE_SIZE = 50;

export default function SponsorshipReview() {
  const [reviewStatus, setReviewStatus] = useState("pending");
  const [exchangeID, setExchangeID] = useState<number | undefined>(undefined);
  // v2.7.4 — promotion-style filter default hides link_only noise.
  const [promotionStyle, setPromotionStyle] = useState<string>("not_link_only");
  // v2.3.3 — pagination. Was missing, so historical confirmed/rejected rows
  // past the first 50 couldn't be reached. Reset to 1 when filters change.
  const [page, setPage] = useState(1);
  const [selectedID, setSelectedID] = useState<number | null>(null);
  // v2.1.8 — multi-select for bulk actions. Independent from `selectedID`
  // (which drives the detail panel) — checkbox tick adds to bulk set; row
  // click still navigates the detail.
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set());
  const qc = useQueryClient();

  const exchangesQ = useQuery({
    queryKey: ["exchanges"],
    queryFn: () => endpoints.exchanges(),
    staleTime: 5 * 60_000,
  });

  const listQ = useQuery({
    queryKey: ["sponsorships", { reviewStatus, exchangeID, promotionStyle, page }],
    queryFn: () =>
      endpoints.sponsorships({
        review_status: reviewStatus,
        exchange_id: exchangeID,
        promotion_style: promotionStyle,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const rows = listQ.data?.data ?? [];
  const hasNext = listQ.data?.next_page !== undefined;
  const selected = useMemo(
    () => rows.find((r) => r.ID === selectedID) ?? rows[0],
    [rows, selectedID],
  );

  const toggleBulk = (id: number) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkMut = useMutation({
    mutationFn: (status: "confirmed" | "rejected") =>
      endpoints.bulkReviewSponsorships({
        ids: Array.from(bulkSelected),
        review_status: status,
      }),
    onSuccess: (resp) => {
      setBulkSelected(new Set());
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "badges"] });
      alert(`已批量${resp.review_status === "confirmed" ? "确认" : "驳回"} ${resp.affected} 条`);
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">推广审核</h1>
          <span className="text-sm text-muted-foreground">
            第 {page} 页 · 本页 {rows.length} 条
          </span>
          <div className="ml-auto flex gap-2">
            <Select
              value={reviewStatus}
              onChange={(e) => {
                setReviewStatus(e.target.value);
                setPage(1);
              }}
              className="w-40"
            >
              {REVIEW_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
            <Select
              value={exchangeID ?? ""}
              onChange={(e) => {
                setExchangeID(e.target.value === "" ? undefined : Number(e.target.value));
                setPage(1);
              }}
              className="w-48"
            >
              <option value="">全部交易所</option>
              {exchangesQ.data?.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.display_name}
                  {ex.is_self ? "（自己）" : ""}
                </option>
              ))}
            </Select>
            <Select
              value={promotionStyle}
              onChange={(e) => {
                setPromotionStyle(e.target.value);
                setPage(1);
              }}
              className="w-36"
              title="v2.7.4 — 推广形态过滤。默认隐藏「挂链型」(KOL 在无关内容描述底部挂 referral),让审核队列只剩值得人看的真实内容型 + 混合型。"
            >
              {PROMOTION_STYLES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
            <a
              href={csvDownloadURL("/api/sponsorships", {
                review_status: reviewStatus,
                exchange_id: exchangeID,
                // v2.7.6 audit — was missing from CSV export, so operators
                // who export from a link_only-hidden view got back a file
                // that included link_only rows. CSV must mirror the UI's
                // active filter set or the export is a lie.
                promotion_style: promotionStyle,
              })}
              className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted"
              title="导出当前筛选的全部推广记录"
            >
              ↓ 导出 CSV
            </a>
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || listQ.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || listQ.isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <aside className="flex w-96 shrink-0 flex-col overflow-hidden border-r border-border bg-background">
          {/* v2.1.8 — bulk action bar (only when selection exists) */}
          {bulkSelected.size > 0 && (
            <div className="flex items-center gap-2 border-b border-border bg-amber-50 px-3 py-2 text-xs">
              <span className="font-medium">已选 {bulkSelected.size} 条</span>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkMut.isPending}
                onClick={() => {
                  if (confirm(`批量确认 ${bulkSelected.size} 条推广?`)) {
                    bulkMut.mutate("confirmed");
                  }
                }}
              >
                全部确认
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkMut.isPending}
                onClick={() => {
                  if (confirm(`批量驳回 ${bulkSelected.size} 条推广?`)) {
                    bulkMut.mutate("rejected");
                  }
                }}
              >
                全部驳回
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={() => setBulkSelected(new Set())}
              >
                清除
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              className="h-3 w-3"
              checked={rows.length > 0 && bulkSelected.size === rows.length}
              ref={(el) => {
                if (el) el.indeterminate = bulkSelected.size > 0 && bulkSelected.size < rows.length;
              }}
              onChange={(e) => {
                if (e.target.checked) setBulkSelected(new Set(rows.map((r) => r.ID)));
                else setBulkSelected(new Set());
              }}
              title="全选/取消全选当前列表"
            />
            <span>批量选择(本页 {rows.length} 条)</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {listQ.isLoading && <p className="p-4 text-sm text-muted-foreground">加载中…</p>}
            {listQ.isError && (
              <p className="p-4 text-sm text-destructive">
                加载失败：{(listQ.error as Error).message}
              </p>
            )}
            {listQ.data && rows.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">没有匹配的推广</p>
            )}
            <ul>
              {rows.map((row) => (
                <SponsorshipListItem
                  key={row.ID}
                  row={row}
                  active={(selected?.ID ?? -1) === row.ID}
                  bulkChecked={bulkSelected.has(row.ID)}
                  onBulkToggle={() => toggleBulk(row.ID)}
                  onClick={() => setSelectedID(row.ID)}
                />
              ))}
            </ul>
          </div>
        </aside>

        <section className="flex-1 overflow-y-auto p-6">
          {selected ? (
            <SponsorshipDetail
              key={selected.ID}
              row={selected}
              onReviewed={() => {
                // v2.4.3 — auto-advance to the next row in the visible list
                // instead of clearing selection. Use the current rows array
                // (the just-reviewed row may disappear after refetch, so we
                // compute "next" BEFORE the data reloads). When at the end,
                // fall back to clearing selection — operator hit the bottom.
                const idx = rows.findIndex((r) => r.ID === selected.ID);
                const nextRow = idx >= 0 && idx + 1 < rows.length ? rows[idx + 1] : null;
                setSelectedID(nextRow?.ID ?? null);
                listQ.refetch();
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">在左侧选择一条推广</p>
          )}
        </section>
      </div>
    </div>
  );
}

function SponsorshipListItem({
  row,
  active,
  bulkChecked,
  onBulkToggle,
  onClick,
}: {
  row: SponsorshipRow;
  active: boolean;
  bulkChecked: boolean;
  onBulkToggle: () => void;
  onClick: () => void;
}) {
  return (
    <li
      className={cn(
        "flex items-stretch border-b border-border transition-colors",
        active ? "bg-muted" : "hover:bg-muted/50",
      )}
    >
      <label
        className="flex shrink-0 items-center justify-center px-2"
        onClick={(e) => e.stopPropagation()}
        title="加入批量选择"
      >
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={bulkChecked}
          onChange={onBulkToggle}
        />
      </label>
      <button
        onClick={onClick}
        className="flex-1 px-3 py-3 text-left"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium">{row.ExchangeDisplayName}</span>
          <span className="flex items-center gap-1 text-[10px] tabular-nums">
            <span
              className={scoreToneClass(row.ConfidenceScore)}
              title={scoreTooltip(row.ConfidenceScore)}
            >
              {row.ConfidenceScore.toFixed(2)}
            </span>
            {/* v2.6.3 — when v2 lags v1, surface the gap so operator sees
                a sibling-demoted or URL-binding-demoted row at a glance. */}
            {row.ConfidenceScoreV2 != null &&
              Math.abs(row.ConfidenceScore - row.ConfidenceScoreV2) > 0.01 && (
                <>
                  <span className="text-muted-foreground">→</span>
                  <span
                    className={scoreToneClass(row.ConfidenceScoreV2)}
                    title={`v2 score: ${scoreTooltip(row.ConfidenceScoreV2)} — v2.6 检测引擎重新评估的低分`}
                  >
                    {row.ConfidenceScoreV2.toFixed(2)}
                  </span>
                </>
              )}
          </span>
        </div>
        <div className="mt-1 line-clamp-1 text-xs text-muted-foreground">
          {row.VideoTitle ?? `Video #${row.VideoID}`}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <PlatformBadge platform={row.ChannelPlatform} />
          <span className="truncate">{row.ChannelName ?? "—"}</span>
          {row.CoOccurringExchangesCount > 1 && (
            <span className="text-[10px] text-amber-600" title="同一条内容里检测到多家交易所(空投/汇总类)">
              · 共现 {row.CoOccurringExchangesCount} 家
            </span>
          )}
          {/* v2.7.4 — promotion-style tag. Defaults to hidden in the
              queue (link_only filtered out) but shows for content_focused
              and mixed so the operator knows what they're reading. The
              LLM shadow column gets a ⚠️ if rule + LLM disagree. */}
          {row.PromotionStyle && (
            <span
              className={cn("text-[10px]", promotionStyleClass(row.PromotionStyle))}
              title={
                row.PromotionStyleLlm && row.PromotionStyleLlm !== row.PromotionStyle
                  ? `规则: ${promotionStyleLabel(row.PromotionStyle)} | LLM: ${promotionStyleLabel(row.PromotionStyleLlm)} — 两者不一致,人工裁定`
                  : `推广形态: ${promotionStyleLabel(row.PromotionStyle)}`
              }
            >
              · {promotionStyleLabel(row.PromotionStyle)}
              {row.PromotionStyleLlm && row.PromotionStyleLlm !== row.PromotionStyle && " ⚠️"}
            </span>
          )}
          {/* v2.6.7 — when ReviewStatus is still pending but reviewed_by is
              set, LLM has already classified this row and left it for human
              confirmation. Operator should prioritize these — they're LLM's
              "looks like a real promo" picks. */}
          {row.ReviewStatus === "pending" && row.ReviewedBy === "system_llm_review" && (
            <span
              className="text-[10px] text-emerald-700 dark:text-emerald-400"
              title="LLM 复核认为这是真推广,留 pending 给运营人工确认"
            >
              · 🤖 LLM 认为真
            </span>
          )}
          {row.IsOurCampaign && (
            <Badge className="ml-auto" variant="info">our</Badge>
          )}
        </div>
        <div className="mt-0.5 text-[10px] text-muted-foreground">
          {row.DetectionMethod} · {fmtDate(row.CreatedAt)}
        </div>
      </button>
    </li>
  );
}

// Detector emits one entry per piece of evidence. See
// internal/detector/engine.go: Signal{Type, Match, Position, Language, URL,
// ReferralCode, BelongsToExchange}. URL + ReferralCode are populated for
// type="referral_link" when the surrounding URL can be parsed out of the
// description. belongs_to_exchange is set by v2.6.0: true means the URL's
// registrable domain matches THIS exchange's Domains, false means the URL
// belongs to a different exchange (signal kept for audit but excluded
// from v2 scoring).
interface DetectionSignal {
  type: string;
  match: string;
  position: string;
  language?: string;
  url?: string;
  referral_code?: string;
  belongs_to_exchange?: boolean;
}

// Friendly label for signal types — anything unmapped falls through verbatim.
const SIGNAL_LABEL: Record<string, string> = {
  domain_match: "域名命中",
  alias_in_title: "别名（标题）",
  alias_in_description: "别名（描述）",
  sponsor_phrase: "推广话术",
  sponsor_phrase_medium: "推广话术（中等）",
  referral_link: "返佣链接",
  disclosure_phrase: "披露声明",
  brand_keyword: "品牌关键词",
  bare_code: "裸促销码",
  // v2.1.8.2 — referral_code 匹配上某个 campaign_channels 行的 code。
  // 因 referral_code 跟 KOL 走(永久,跨多 campaign),仅作 hint 不自动绑;
  // 表单的 "新建投放" 路径会用这个 code 预填 `referral_code` 字段。
  referral_code_match: "返佣码匹配",
  campaign_referral_code: "campaign 码（旧）",
  // v2.6.1 — pipeline 在 score 计算后扫一遍同 video 的其它 sponsorship,
  // 如果有强势真推广(v2 >= 0.85),把这条弱兄弟(v2 < 0.60)砸到 ScoreLow
  // 并 emit 这个 signal,运营审核时可以一眼看到"我被同视频另一家盖过了"
  sibling_demoted: "同视频强势兄弟降级",
  // v2.6.9 — URL 命中返佣关键字但 eTLD+1 不属于任何已知交易所
  // (eg. partner.bitget.cloud 冒充 bitget.com,bsmkweb.cc 冒充 binance.com)
  // 红色警示;视频里出现这种链接通常代表 KOL 在用钓鱼站 / 未授权聚合站导流
  suspicious_proxy: "⚠️ 疑似仿冒/代理域名",
};

function SponsorshipDetail({
  row,
  onReviewed,
}: {
  row: SponsorshipRow;
  onReviewed: () => void;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(row.ReviewNotes ?? "");
  const [error, setError] = useState<string | null>(null);

  // Identify whether this sponsorship is for our own (Bitbase) exchange so
  // we can offer the campaign-binding wizard on confirm.
  const exchangesQ = useQuery({
    queryKey: ["exchanges"],
    queryFn: () => endpoints.exchanges(),
    staleTime: 5 * 60_000,
  });
  const isSelf = (exchangesQ.data ?? []).some(
    (e) => e.is_self && e.code === row.ExchangeCode,
  );

  // Extract referral_code from detection signals (for auto-fill in the
  // "新建投放" path).
  const signals = decodeJSONB<DetectionSignal[]>(row.DetectionSignals) ?? [];
  const extractedReferralCode = signals.find((s) => s.referral_code)?.referral_code ?? "";
  const extractedURL = signals.find((s) => s.url)?.url ?? "";

  // v2.6.11 — pull cached third-party domain reputations once; map by
  // domain so each suspicious_proxy signal can render its verdict
  // inline. Stale-while-revalidate 5 min — table changes slowly.
  const domainRepQ = useQuery({
    queryKey: ["domain-reputation"],
    queryFn: () => endpoints.domainReputation(),
    staleTime: 5 * 60_000,
  });
  const domainReputation = (() => {
    const map = new Map<string, DomainReputationItem>();
    for (const r of domainRepQ.data ?? []) {
      map.set(r.domain.toLowerCase(), r);
    }
    return map;
  })();

  // Campaign action state — only used in the self-sponsorship confirm flow.
  type ActionMode = "create" | "link" | "skip";
  const initialMode: ActionMode =
    isSelf && !row.IsOurCampaign ? "create" : "skip";
  const [actionMode, setActionMode] = useState<ActionMode>(initialMode);
  const [linkCampaignID, setLinkCampaignID] = useState<number | null>(null);
  const [newCampaignName, setNewCampaignName] = useState(
    `Auto: sponsorship #${row.ID}`,
  );
  const [newCampaignCode, setNewCampaignCode] = useState(extractedReferralCode);
  // Channel is now auto-derived from the sponsorship row (v1.3.1 added
  // channel_id to the list query). The ChannelPicker stays mounted as a
  // safety override — operator can change it if the video was reposted to
  // a different channel or anything else weird.
  const [newCampaignChannelID, setNewCampaignChannelID] = useState<number | null>(
    row.ChannelID || null,
  );

  const review = useMutation({
    mutationFn: (body: ReviewSponsorshipRequest) =>
      endpoints.reviewSponsorship(row.ID, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      onReviewed();
    },
    onError: (err) => setError((err as Error).message),
  });

  // v2.6.13 — operator-set manual phishing verdict. Used by the
  // inline "标记为钓鱼" button on suspicious_proxy signals; the next
  // llm_review_pending cron tick will then hit the reputation
  // shortcut and auto-reject every pending row using that domain.
  const markPhishing = useMutation({
    mutationFn: (domain: string) =>
      endpoints.setDomainManualVerdict(
        domain,
        "phishing",
        "operator verified lookalike via SponsorshipReview UI",
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["domain-reputation"] });
    },
    onError: (err) => setError((err as Error).message),
  });

  // v2.6.13 — one-click KOL blacklist. Sets channel lifecycle to
  // 'blacklisted'; the backend's ApplyArchive helper then auto-rejects
  // ALL pending sponsorships for the channel and closes any open eval
  // tasks. Operator confirmation modal mandatory — this is a
  // hard-to-reverse decision.
  const blacklistChannel = useMutation({
    mutationFn: (channelID: number) =>
      endpoints.updateChannelLifecycle(channelID, {
        lifecycle_status: "blacklisted",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      onReviewed();
    },
    onError: (err) => setError((err as Error).message),
  });

  function buildConfirmPayload(): ReviewSponsorshipRequest {
    const base: ReviewSponsorshipRequest = {
      review_status: "confirmed",
      review_notes: notes.trim() || undefined,
    };
    if (!isSelf || row.IsOurCampaign) return base;
    let action: CampaignActionPayload;
    if (actionMode === "skip") {
      action = { type: "skip" };
    } else if (actionMode === "link") {
      if (!linkCampaignID) throw new Error("请选择一个已有投放");
      if (!newCampaignChannelID) throw new Error("请选择频道");
      action = {
        type: "link_channel",
        link_campaign_id: linkCampaignID,
        create_campaign: {
          channel_id: newCampaignChannelID,
          referral_code: newCampaignCode.trim() || undefined,
        },
      };
    } else {
      if (!newCampaignChannelID) throw new Error("请选择频道");
      action = {
        type: "create",
        create_campaign: {
          name: newCampaignName.trim() || `Auto: sponsorship #${row.ID}`,
          channel_id: newCampaignChannelID,
          referral_code: newCampaignCode.trim() || undefined,
          status: "active",
          notes: `Auto-created from sponsorship #${row.ID}${extractedURL ? `\nURL: ${extractedURL}` : ""}`,
        },
      };
    }
    base.campaign_action = action;
    return base;
  }

  function onConfirm() {
    setError(null);
    try {
      review.mutate(buildConfirmPayload());
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function onReject() {
    setError(null);
    review.mutate({
      review_status: "rejected",
      review_notes: notes.trim() || undefined,
    });
  }

  const done = row.ReviewStatus !== "pending";

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-xl font-semibold">{row.ExchangeDisplayName}</h2>
            <p className="text-xs text-muted-foreground">
              code: {row.ExchangeCode} · sponsorship #{row.ID}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant={decisionVariant(row.ReviewStatus)}>
              {decisionLabel(row.ReviewStatus)}
            </Badge>
            <Badge variant={confidenceVariant(row.Confidence)}>
              置信度：{confidenceLabel(row.Confidence)} ·{" "}
              {/* v2.4.3 — colorize the numeric score so a 0.55 doesn't read
                  the same as 0.95 at a glance. Thresholds match detector
                  pipeline auto-review gates (auto_confirm_score 0.50, the
                  audit query's "high band" cutoff 0.85). */}
              <span
                className={scoreToneClass(row.ConfidenceScore)}
                title={scoreTooltip(row.ConfidenceScore)}
              >
                {row.ConfidenceScore.toFixed(2)} {scoreBandLabel(row.ConfidenceScore)}
              </span>
              {row.ConfidenceScoreV2 != null &&
                Math.abs(row.ConfidenceScore - row.ConfidenceScoreV2) > 0.01 && (
                  <>
                    <span className="mx-1 text-muted-foreground">→</span>
                    <span
                      className={scoreToneClass(row.ConfidenceScoreV2)}
                      title={`v2 重评分: ${scoreTooltip(row.ConfidenceScoreV2)}。v2.6 检测引擎按 URL 归属 + 同视频强势兄弟调整得到的分,与 v1 不一致时这条已自动路由 pending。`}
                    >
                      {row.ConfidenceScoreV2.toFixed(2)} {scoreBandLabel(row.ConfidenceScoreV2)}
                    </span>
                  </>
                )}
            </Badge>
            <Badge
              variant={row.CoOccurringExchangesCount === 1 ? "info" : "outline"}
              title="同一条内容里检测到的交易所家数。1 = 独家推广;2+ = 共现(空投/汇总/对比)"
            >
              {row.CoOccurringExchangesCount === 1
                ? "独家"
                : `共现 ${row.CoOccurringExchangesCount} 家`}
            </Badge>
            {row.IsOurCampaign && <Badge variant="info">our campaign</Badge>}
          </div>
        </div>
      </div>

      <Section title="内容">
        <p className="text-sm font-medium">{row.VideoTitle ?? `Video #${row.VideoID}`}</p>
        <p className="text-xs text-muted-foreground">
          频道：
          <Link
            to={`/channels/${row.ChannelID}`}
            className="text-primary hover:underline"
          >
            {row.ChannelName ?? "—"}
          </Link>{" "}
          · 平台：{platformLabel(row.ChannelPlatform)}
          {/* v2.6.13 — one-click KOL blacklist. Sets channel lifecycle
              to 'blacklisted' + auto-rejects every pending sponsorship
              for that channel via the same ApplyArchive helper the
              archive flow uses. Hidden after operator decision is in
              (done=true). */}
          {!done && row.ChannelID && (
            <button
              type="button"
              className="ml-3 rounded border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
              onClick={() => {
                const name = row.ChannelName ?? `#${row.ChannelID}`;
                if (
                  confirm(
                    `拉黑频道 ${name}?\n\n会同时:\n` +
                      `· 把 lifecycle_status 设为 blacklisted\n` +
                      `· 自动驳回该频道全部 pending 推广\n` +
                      `· 关闭未完成的评估任务\n\n` +
                      `这是个偏激进的动作。确认?`,
                  )
                ) {
                  blacklistChannel.mutate(row.ChannelID);
                }
              }}
              disabled={blacklistChannel.isPending}
              title="拉黑此 KOL — 自动驳回该频道全部 pending 推广 + 后续 detector 跳过此频道"
            >
              🚫 拉黑此 KOL
            </button>
          )}
        </p>
        {row.VideoUrl && (
          <a
            href={row.VideoUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline break-all"
          >
            {row.VideoUrl}
          </a>
        )}
      </Section>

      <Section title="检测证据">
        <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <DL label="检测方法">{row.DetectionMethod}</DL>
          <DL label="检测时间">{fmtTimestamp(row.CreatedAt)}</DL>
        </dl>
        {signals.length === 0 ? (
          <p className="text-xs text-muted-foreground">无法解析 detection_signals</p>
        ) : (
          <ul className="space-y-1.5">
            {signals.map((s, i) => {
              // v2.6.0+ — referral_link signals where the URL's registrable
              // domain does NOT match this exchange's Domains are kept
              // visible for audit context but visually de-emphasized + tagged
              // "非本所", so the operator doesn't read another exchange's URL
              // as evidence for the current row.
              const isForeign =
                s.type === "referral_link" && s.belongs_to_exchange === false;
              // v2.6.9 — suspicious_proxy is a brand-lookalike / unofficial
              // aggregator URL. Strong red flag; flag it visibly so the
              // operator can decide to reject + (next version) blacklist KOL.
              const isSuspicious = s.type === "suspicious_proxy";
              // v2.6.11 — pull verdict from the threatintel cache (VT + SB).
              // Verdict severity may upgrade the visual: confirmed phishing →
              // brighter red, suspected → orange, clean → soft green / no
              // override, missing → keep base styling (cron will catch it).
              const verdict = isSuspicious && s.match
                ? domainReputation.get(s.match.toLowerCase())
                : undefined;
              const sev = verdict?.severity;
              const cardClass = isSuspicious
                ? sev === "phishing_confirmed"
                  ? "rounded border-2 border-red-600 bg-red-50/80 px-2 py-1.5 text-xs dark:bg-red-950/40"
                  : sev === "phishing_suspected"
                    ? "rounded border border-orange-500/60 bg-orange-50/60 px-2 py-1.5 text-xs dark:bg-orange-950/30"
                    : sev === "clean"
                      ? "rounded border border-emerald-500/40 bg-emerald-50/40 px-2 py-1.5 text-xs dark:bg-emerald-950/20"
                      : "rounded border border-red-500/60 bg-red-50/60 px-2 py-1.5 text-xs dark:bg-red-950/30"
                : isForeign
                  ? "rounded border border-dashed border-border bg-muted/10 px-2 py-1.5 text-xs opacity-60"
                  : "rounded border border-border bg-muted/20 px-2 py-1.5 text-xs";
              return (
              <li key={i} className={cardClass}>
                <div className="flex flex-wrap items-baseline gap-2">
                  <Badge variant="outline">
                    {SIGNAL_LABEL[s.type] ?? s.type}
                  </Badge>
                  {isForeign && (
                    <Badge
                      variant="outline"
                      className="border-amber-500 text-amber-700"
                    >
                      非本所 URL
                    </Badge>
                  )}
                  <span className="text-muted-foreground">{s.position}</span>
                  <code className="break-all text-foreground">{s.match}</code>
                  {s.language && (
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {s.language}
                    </span>
                  )}
                </div>
                {(s.url || s.referral_code) && (
                  <div className="mt-1 space-y-0.5 pl-1 text-[11px]">
                    {s.referral_code && (
                      <div>
                        <span className="text-muted-foreground">推广码：</span>
                        <code className="rounded bg-amber-50 px-1 text-amber-700">
                          {s.referral_code}
                        </code>
                      </div>
                    )}
                    {s.url && (
                      <div className="truncate">
                        <span className="text-muted-foreground">URL：</span>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="break-all text-primary hover:underline"
                        >
                          {s.url}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {/* v2.6.11 — threat-intel verdict inline on
                    suspicious_proxy signals. Compact 1-liner so the
                    operator can see VT / SB outcome without leaving
                    the page. */}
                {isSuspicious && verdict && (
                  <div className="mt-1 pl-1 text-[11px]">
                    {verdict.severity === "phishing_confirmed" && (
                      <span className="font-medium text-red-700 dark:text-red-300">
                        🚨 已确认钓鱼
                      </span>
                    )}
                    {verdict.severity === "phishing_suspected" && (
                      <span className="font-medium text-orange-700 dark:text-orange-300">
                        ⚠️ 疑似钓鱼
                      </span>
                    )}
                    {verdict.severity === "clean" && (
                      <span className="text-emerald-700 dark:text-emerald-300">
                        ✓ 无安全报告(但仍非本所域名)
                      </span>
                    )}
                    {(verdict.vt_total ?? 0) > 0 && (
                      <span className="ml-2 text-muted-foreground">
                        VT: {verdict.vt_malicious ?? 0}/{verdict.vt_total} 厂商报告恶意
                      </span>
                    )}
                    {verdict.sb_threat_types && verdict.sb_threat_types.length > 0 && (
                      <span className="ml-2 text-red-700 dark:text-red-300">
                        Google SB: {verdict.sb_threat_types.join(", ")}
                      </span>
                    )}
                  </div>
                )}
                {isSuspicious && !verdict && (
                  <div className="mt-1 pl-1 text-[10px] text-muted-foreground">
                    威胁情报未扫描(下次 cron 03:30 UTC 自动查)
                  </div>
                )}
                {/* v2.6.13 — operator manual phishing flag. Shown unless
                    domain is already manually marked phishing. One click
                    sets the override; reputationVerdict() picks it up on
                    next LLM cron tick and auto-rejects all pending rows
                    using this URL. */}
                {isSuspicious && s.match && verdict?.manual_verdict !== "phishing" && (
                  <div className="mt-1 pl-1 text-[11px]">
                    <button
                      type="button"
                      className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
                      onClick={() => {
                        if (
                          confirm(
                            `标记 ${s.match} 为钓鱼站?\n\n所有 pending 推广里引用该域名的 URL 都会在下次 LLM cron(每小时 :05)自动驳回。可以随后在系统页清除标记。`,
                          )
                        ) {
                          markPhishing.mutate(s.match.toLowerCase());
                        }
                      }}
                      disabled={markPhishing.isPending}
                      title="标记此域名为钓鱼/仿冒站。下次 LLM cron 会自动驳回所有引用它的 pending 推广。"
                    >
                      🚩 标记为钓鱼站
                    </button>
                  </div>
                )}
                {isSuspicious && verdict?.manual_verdict === "phishing" && (
                  <div className="mt-1 pl-1 text-[11px] text-red-700 dark:text-red-300">
                    🚩 已被 {verdict.manual_by ?? "运营"} 手动标记为钓鱼
                    {verdict.manual_at && (
                      <span className="ml-1 text-muted-foreground">
                        · {fmtTimestamp(verdict.manual_at)}
                      </span>
                    )}
                  </div>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </Section>

      {done ? (
        <div className="rounded-md border border-border bg-muted/30 p-4 text-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="text-muted-foreground">
              已审核：{row.ReviewedBy ?? "—"} 于 {fmtTimestamp(row.ReviewedAt)}
              {row.ReviewNotes && (
                <p className="mt-1 text-xs">备注：{row.ReviewNotes}</p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (
                  confirm(
                    `撤销当前审核结论（${row.ReviewStatus}），重新放回待审核队列？`,
                  )
                ) {
                  review.mutate({ review_status: "pending" });
                }
              }}
              disabled={review.isPending}
            >
              撤销审核
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-background p-4">
          <h3 className="mb-3 text-sm font-semibold">审核</h3>

          {isSelf && !row.IsOurCampaign && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
              <p className="mb-2 font-medium text-amber-900">
                这是 Bitbase 推广，确认时建议绑定到投放：
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="campaign-action"
                    checked={actionMode === "create"}
                    onChange={() => setActionMode("create")}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="font-medium">新建投放（推荐）</span>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      auto-create a new campaign record + link this sponsorship
                    </div>
                    {actionMode === "create" && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <label className="col-span-2 block">
                          <span className="mb-0.5 block text-[10px] text-muted-foreground">投放名称</span>
                          <Input
                            value={newCampaignName}
                            onChange={(e) => setNewCampaignName(e.target.value)}
                          />
                        </label>
                        <ChannelFieldOrPicker
                          row={row}
                          value={newCampaignChannelID}
                          onChange={setNewCampaignChannelID}
                        />
                        <label className="col-span-2 block">
                          <span className="mb-0.5 block text-[10px] text-muted-foreground">
                            返佣码 {extractedReferralCode && "(从推广 URL 解析到)"}
                          </span>
                          <Input
                            value={newCampaignCode}
                            onChange={(e) => setNewCampaignCode(e.target.value)}
                            placeholder="BB_KOL..."
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="campaign-action"
                    checked={actionMode === "link"}
                    onChange={() => setActionMode("link")}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="font-medium">关联到已有投放</span>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      会在该投放下为这个频道新建一条 KOL 合约（如果还没有的话）
                    </div>
                    {actionMode === "link" && (
                      <div className="mt-2 grid grid-cols-1 gap-2">
                        <LinkCampaignSelector
                          value={linkCampaignID}
                          onChange={setLinkCampaignID}
                        />
                        <ChannelFieldOrPicker
                          row={row}
                          value={newCampaignChannelID}
                          onChange={setNewCampaignChannelID}
                        />
                        <label className="block">
                          <span className="mb-0.5 block text-[10px] text-muted-foreground">
                            返佣码 {extractedReferralCode && "(从推广 URL 解析到)"}
                          </span>
                          <Input
                            value={newCampaignCode}
                            onChange={(e) => setNewCampaignCode(e.target.value)}
                            placeholder="BB_KOL..."
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="campaign-action"
                    checked={actionMode === "skip"}
                    onChange={() => setActionMode("skip")}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="font-medium">不关联</span>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      organic 推广 / 后面再补，sponsorship 保留为孤立的 confirmed
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          <label className="mb-1 block text-xs text-muted-foreground">备注（可选）</label>
          <textarea
            className="mb-3 h-20 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="写下审核结论的依据，会记入 audit_log"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {error && <p className="mb-2 text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <Button onClick={onConfirm} disabled={review.isPending}>
              {review.isPending ? "处理中…" : "确认推广"}
            </Button>
            <Button variant="destructive" onClick={onReject} disabled={review.isPending}>
              判定误报
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// v2.4.3 — score band helpers. Map the numeric confidence_score onto a
// qualitative tone + label so operators not steeped in ML thresholds can
// tell at a glance whether 0.55 is "barely past auto-confirm" or 0.95 is
// "very high". Thresholds match the detector pipeline + LLM audit query:
//   < 0.65 = borderline (auto-confirmed only because threshold is 0.50)
//   0.65 - 0.85 = mid
//   >= 0.85 = high
function scoreToneClass(s: number): string {
  if (s >= 0.85) return "text-emerald-700 font-medium";
  if (s >= 0.65) return "text-amber-700";
  return "text-orange-700"; // borderline — likely deserved manual look
}

function scoreBandLabel(s: number): string {
  if (s >= 0.85) return "高";
  if (s >= 0.65) return "中";
  return "边缘"; // "borderline" — just over the auto-confirm threshold
}

function scoreTooltip(s: number): string {
  if (s >= 0.85) return `高置信(>=0.85),自动确认放心`;
  if (s >= 0.65) return `中置信(0.65-0.85),建议手动看一眼`;
  return `边缘置信(${s.toFixed(2)}<0.65),刚过 auto-confirm 阈值 0.50,值得手动复核`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-background p-4">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function DL({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm break-all">{children}</dd>
    </div>
  );
}

// LinkCampaignSelector — small native-select dropdown over the existing
// campaigns. Used only in the "关联到已有投放" path of the confirm wizard.
// Channel-scoped picker would be nicer but campaigns list is small (< 50);
// a flat list keeps the wizard compact.
// ChannelFieldOrPicker — when the sponsorship row already carries a
// channel_id (v1.3.1+ joins it in), show the channel name as confirmation
// text with a small "切换" affordance. Falls back to the full ChannelPicker
// only when the row didn't carry it or the operator wants to override.
function ChannelFieldOrPicker({
  row,
  value,
  onChange,
}: {
  row: SponsorshipRow;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [override, setOverride] = useState(false);
  const autoMatched = row.ChannelID > 0 && value === row.ChannelID && !override;
  if (autoMatched) {
    return (
      <label className="col-span-2 block">
        <span className="mb-0.5 block text-[10px] text-muted-foreground">频道（自动匹配）</span>
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          <span className="font-medium">
            {row.ChannelName ?? `Channel #${row.ChannelID}`}
          </span>
          <span className="text-xs text-muted-foreground">
            ({platformLabel(row.ChannelPlatform)})
          </span>
          <button
            type="button"
            className="ml-auto text-[10px] text-primary hover:underline"
            onClick={() => setOverride(true)}
          >
            切换频道
          </button>
        </div>
      </label>
    );
  }
  return (
    <label className="col-span-2 block">
      <span className="mb-0.5 block text-[10px] text-muted-foreground">频道 *</span>
      <ChannelPicker
        value={value}
        onChange={(id) => onChange(id)}
        placeholder="挑这条推广所在的频道"
      />
    </label>
  );
}

function LinkCampaignSelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const q = useQuery({
    queryKey: ["campaigns-for-link"],
    queryFn: () => endpoints.campaigns({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <Select
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : Number(e.target.value))
      }
    >
      <option value="">— 选投放 —</option>
      {(q.data?.data ?? []).map((c) => (
        <option key={c.id} value={c.id}>
          #{c.id} · {c.name}
          {c.channel_count > 0 ? ` · ${c.channel_count} KOL` : ""}
        </option>
      ))}
    </Select>
  );
}
